import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// Attach access token from context if present
let _accessToken = null;
export function setAccessToken(t) {
  _accessToken = t;
}
api.interceptors.request.use((cfg) => {
  if (_accessToken) {
    cfg.headers = cfg.headers || {};
    cfg.headers.Authorization = `Bearer ${_accessToken}`;
  }
  return cfg;
});

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Ein Fehler ist aufgetreten.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

/**
 * Normalize a decimal string entered by the user:
 *   - replaces German comma with a dot ("8,5" → "8.5")
 *   - strips currency signs, unit hints, whitespace and thousand separators
 *     ("1.234,56 €" → "1234.56")
 *   - returns empty string for null / undefined / empty input
 *
 * The returned value is still a string so React inputs keep their exact
 * cursor position. Convert to `Number(...)` only right before sending.
 */
export function normalizeDecimal(v) {
  if (v == null) return "";
  let s = String(v).trim();
  if (!s) return "";
  // Drop anything that isn't a digit / sign / decimal separator / whitespace
  s = s.replace(/[^\d\-,.\s]/g, "");
  s = s.replace(/\s+/g, "");
  // German thousands + decimal: "1.234,56" → "1234.56"
  if (s.includes(",") && s.includes(".")) {
    s = s.replace(/\./g, "").replace(",", ".");
  } else {
    s = s.replace(",", ".");
  }
  return s;
}

/** Placeholder text hint for decimal number inputs. */
export const DECIMAL_HINT = "z.B. 8.5 (Punkt)";

/**
 * Container units used in the Gebinde-Verwaltung. `kg`/`g` are solids
 * measured in grams (`base="g"`), `l`/`ml` are liquids measured in
 * milliliters (`base="ml"`). Factor tells how many base-units live in
 * one container unit (e.g. 1 kg → 1000 g, 1 l → 1000 ml).
 */
export const CONTAINER_UNIT_META = {
  kg: { factor: 1000, base: "g", kind: "solid" },
  g:  { factor: 1,    base: "g", kind: "solid" },
  l:  { factor: 1000, base: "ml", kind: "liquid" },
  ml: { factor: 1,    base: "ml", kind: "liquid" },
};

/** Returns the multiplier that converts a container unit to its base
 * (grams for solids, milliliters for liquids). Unknown units → 1. */
export function containerFactor(unit) {
  return CONTAINER_UNIT_META[unit]?.factor ?? 1;
}

/** Base unit label ("g" for kg/g, "ml" for l/ml). Defaults to "g". */
export function containerBase(unit) {
  return CONTAINER_UNIT_META[unit]?.base ?? "g";
}

/** Convert a container amount into its base unit (e.g. 5 l → 5000 ml). */
export function containerToBase(size, unit) {
  const n = Number(size);
  if (!isFinite(n)) return 0;
  return n * containerFactor(unit);
}

/** Physical kind of an item's stored quantity: "liquid" (base ml) or
 * "solid" (base g). Anything non-metric (e.g. `stk`) is treated as
 * "count" so downstream code can short-circuit unit conversion. */
export function itemBaseKind(item) {
  const u = (item?.unit || "").toLowerCase();
  if (u === "l" || u === "ml") return "liquid";
  if (u === "kg" || u === "g") return "solid";
  return "count";
}

/** Item base unit label ("g" | "ml" | item.unit). */
export function itemBase(item) {
  const k = itemBaseKind(item);
  if (k === "liquid") return "ml";
  if (k === "solid") return "g";
  return item?.unit || "";
}

/**
 * Convert a quantity from `fromUnit` into `toUnit` using the standard
 * metric ratios (kg↔g and l↔ml). Same-unit or unknown-unit inputs pass
 * through unchanged; cross-kind conversions (mass↔volume) also pass
 * through because they require an item density and belong in
 * `convertIngredientToItemBase`.
 *
 * Used by the stock-booking form to translate the user's entry into the
 * unit the item is stored in (Runde 125.6). Prior code hard-coded a
 * `× 1000` for kg/l inputs which corrupted stocks whenever an item was
 * itself defined in kg or l.
 */
export function convertQty(qty, fromUnit, toUnit) {
  const q = Number(qty);
  if (!isFinite(q)) return 0;
  const f = String(fromUnit || "").trim().toLowerCase();
  const t = String(toUnit || "").trim().toLowerCase();
  if (!f || !t || f === t) return q;
  const solids = new Set(["kg", "g"]);
  const liquids = new Set(["l", "ml"]);
  const factor = { kg: 1000, g: 1, l: 1000, ml: 1, stk: 1 };
  const sameKind = (solids.has(f) && solids.has(t)) || (liquids.has(f) && liquids.has(t));
  if (!sameKind) return q;
  return q * (factor[f] / factor[t]);
}

/**
 * Multiplier for recipe lines: `qty` counts units of the finished product.
 * Mirrors backend `_shared.recipe_factor` (no weight: 1 unit = 1 recipe output).
 */
export function recipeFactor(qty, outputQuantity, outputUnit, weightG) {
  const q = Number(qty) || 0;
  const outQty = Number(outputQuantity) || 1;
  const u = String(outputUnit || "").trim().toLowerCase();
  if (!["g", "ml", "kg", "l"].includes(u)) return q / outQty;
  const w = Number(weightG) || 0;
  const base = u === "ml" || u === "l" ? "ml" : "g";
  const perUnit = w > 0 ? convertQty(w, base, u) : outQty;
  return (q * perUnit) / outQty;
}

/**
 * Convert an ingredient quantity to the item's stored base unit.
 *
 * Returns `{ value, needsDensity }`:
 *   - `value` is the amount in the item's base unit (g / ml / stk).
 *   - `needsDensity` is true when the ingredient unit's physical kind
 *     differs from the item's kind (e.g. `ml` vs. solid item) AND no
 *     density is set on the item. `value` is `null` in that case so the
 *     caller can show a warning.
 */
export function convertIngredientToItemBase(qty, ingUnit, item) {
  const q = Number(qty);
  if (!isFinite(q) || !item) return { value: 0, needsDensity: false };
  const u = (ingUnit || "").toLowerCase();
  if (u === "stk") return { value: q, needsDensity: false };
  const ingKind = (u === "l" || u === "ml") ? "liquid"
                  : (u === "kg" || u === "g") ? "solid" : "count";
  const ingBaseFactor = (u === "kg" || u === "l") ? 1000 : 1;
  const inIngBase = q * ingBaseFactor;
  const itemKind = itemBaseKind(item);
  if (ingKind === itemKind) return { value: inIngBase, needsDensity: false };
  const d = Number(item.density);
  if (!isFinite(d) || d <= 0) return { value: null, needsDensity: true };
  // ingredient is liquid, item is solid → mass_g = ml * density
  // ingredient is solid, item is liquid → volume_ml = g / density
  const value = ingKind === "liquid" ? inIngBase * d : inIngBase / d;
  return { value, needsDensity: false };
}

/** €/base-unit cost for a raw item (€ per g or per ml).
 *
 * Preferred formula: `purchase_price / (container_size × container_factor)`.
 * The item's `purchase_price` represents the price paid for **one full
 * container** (e.g. €3.59 for a 400 g bag → €0.008975/g). If no container
 * size is known we fall back to interpreting `purchase_price` as €/unit
 * (kg/l → divide by 1000, everything else → per base unit).
 */
export function itemCostPerBase(item) {
  const price = Number(item?.purchase_price);
  if (!isFinite(price) || price <= 0) return 0;
  const cSize = Number(item?.container_size);
  if (isFinite(cSize) && cSize > 0) {
    const gebindeInBase = containerToBase(cSize, item?.container_unit || "kg");
    if (gebindeInBase > 0) return price / gebindeInBase;
  }
  const u = (item?.unit || "").toLowerCase();
  const factor = (u === "kg" || u === "l") ? 1000 : 1;
  return price / factor;
}


/**
 * Fetch a PDF via authenticated axios request, then open it in a new tab.
 * Fixes issues where window.open on a plain API URL loses cookies in some
 * browser/iframe contexts and returns 401.
 * Supports GET (default) or POST with a JSON body.
 */
export async function openPdf(url, { method = "GET", body = null } = {}) {
  const cfg = { responseType: "blob" };
  const res = method === "POST"
    ? await api.post(url, body, cfg)
    : await api.get(url, cfg);
  const blob = new Blob([res.data], { type: "application/pdf" });
  const objectUrl = URL.createObjectURL(blob);
  const win = window.open(objectUrl, "_blank");
  // fallback: same-tab if popup blocked
  if (!win) window.location.href = objectUrl;
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
  return objectUrl;
}


/** Download a URL as a CSV file with a suggested filename. */
export async function downloadCsv(url, filename) {
  const res = await api.get(url, { responseType: "blob" });
  const objectUrl = URL.createObjectURL(new Blob([res.data], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename || "export.csv";
  a.click();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 30000);
}

/** POST to a URL with JSON body and download the response as a file with a suggested filename. */
export async function postDownload(url, body, filename, mime = "application/zip") {
  const res = await api.post(url, body, { responseType: "blob" });
  const objectUrl = URL.createObjectURL(new Blob([res.data], { type: mime }));
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename || "download";
  a.click();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 30000);
  return res;
}

export default api;
